import { t } from "@lingui/macro";
import { Brain, CaretDown, CaretUp, CheckCircle, CurrencyDollar, FileText, Lightbulb, Lightning, Target, TrendUp, Warning, Wrench } from "@phosphor-icons/react";
import { TOKEN_COSTS } from "@stylingcv/config";
import { Button, Card, CardContent, CardHeader, CardTitle, Label, Textarea } from "@stylingcv/ui";
import { useState } from "react";

import { EnhancedProgressIndicator } from "@/client/components/enhanced-progress-indicator";
import { ModernResumeAnalysisDashboard } from "@/client/components/modern-resume-analysis-dashboard";
import { useTokenModal } from "@/client/contexts/token-modal-context";
import { useAdvancedResumeAnalysis } from "@/client/hooks/smart-ai/use-advanced-resume-analysis";
import { toast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";
import { useCentralizedTokens } from "@/client/hooks/use-centralized-tokens";

export const AnalysisSection = () => {
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isStartingAnalysis, setIsStartingAnalysis] = useState(false);
  const [jobDescription, setJobDescription] = useState("");
  const [showMarketDetails, setShowMarketDetails] = useState(false);
  const [analysisSteps, setAnalysisSteps] = useState<
    {
      id: string;
      title: string;
      description: string;
      status: "pending" | "in-progress" | "completed" | "error";
      progress?: number;
    }[]
  >([
    {
      id: "1",
      title: t`Scanning Resume`,
      description: t`Reading resume content...`,
      status: "pending",
    },
    {
      id: "2",
      title: t`Extracting Keywords`,
      description: t`Analyzing job description...`,
      status: "pending",
      progress: 0,
    },
    {
      id: "3",
      title: t`Comparing Keywords`,
      description: t`Matching resume to JD...`,
      status: "pending",
    },
    {
      id: "4",
      title: t`Generating Insights`,
      description: t`Creating actionable suggestions...`,
      status: "pending",
    },
    {
      id: "5",
      title: t`Analysis Complete`,
      description: t`Preparing your results...`,
      status: "pending",
    },
  ]);
  const { resume } = useResumeStore();
  const { loading, analysisData, analyzeResume, clearAnalysis } = useAdvancedResumeAnalysis();
  const { tokenBalance } = useCentralizedTokens();
  const { showTokenModal } = useTokenModal();

  const cost = TOKEN_COSTS.ADVANCED_RESUME_ANALYSIS;

  // Check if we have Market DNA from the AI generator
  const hasMarketDNA = !!(resume.data?.metadata?.marketContext);
  const marketDNA = resume.data?.metadata?.marketContext;

  const updateStep = (stepId: string, updates: Partial<(typeof analysisSteps)[0]>) => {
    setAnalysisSteps((steps) =>
      steps.map((step) => (step.id === stepId ? { ...step, ...updates } : step)),
    );
  };

  const handleQuickAnalysis = async () => {
    if (!resume.data) return;

    // Check if headline/job title is provided
    const jobTitle = resume.data.basics?.headline;
    if (!jobTitle || jobTitle.trim() === "") {
      toast({
        title: t`Job Title Required`,
        description: t`Please add a headline/job title in your resume's basic information section before starting the analysis.`,
        variant: "error",
      });
      return;
    }

    // Check tokens first before showing loader
    if (tokenBalance < cost) {
      showTokenModal({
        requiredTokens: cost,
        currentTokens: tokenBalance,
        featureName: "Advanced Resume Analysis",
      });
      return;
    }

    setIsStartingAnalysis(true);

    // Reset steps - different steps if using Market DNA
    if (hasMarketDNA) {
      setAnalysisSteps([
        {
          id: "1",
          title: t`Loading Market DNA`,
          description: t`Using saved market intelligence...`,
          status: "pending",
        },
        {
          id: "2",
          title: t`Context-Aware Analysis`,
          description: t`Matching against market truth...`,
          status: "pending",
          progress: 0,
        },
        {
          id: "3",
          title: t`Calculating Scores`,
          description: t`JD Match + Market Relevance + ATS Ready...`,
          status: "pending",
        },
        {
          id: "4",
          title: t`Generating Insights`,
          description: t`Creating actionable suggestions...`,
          status: "pending",
        },
        {
          id: "5",
          title: t`Analysis Complete`,
          description: t`Preparing your results...`,
          status: "pending",
        },
      ]);
    } else {
      setAnalysisSteps([
        {
          id: "1",
          title: t`Scanning Resume`,
          description: t`Reading resume content...`,
          status: "pending",
        },
        {
          id: "2",
          title: t`ATS Analysis`,
          description: t`Checking ATS compatibility...`,
          status: "pending",
          progress: 0,
        },
        {
          id: "3",
          title: t`Content Analysis`,
          description: t`Analyzing content quality...`,
          status: "pending",
        },
        {
          id: "4",
          title: t`Generating Score`,
          description: t`Calculating final scores...`,
          status: "pending",
        },
        {
          id: "5",
          title: t`Analysis Complete`,
          description: t`Preparing recommendations...`,
          status: "pending",
        },
      ]);
    }

    try {
      // Step 1: Scanning
      updateStep("1", { status: "in-progress" });
      await new Promise((resolve) => setTimeout(resolve, 800));
      updateStep("1", { status: "completed" });

      // Step 2: Extracting Keywords with progress
      updateStep("2", { status: "in-progress", progress: 0 });
      for (let i = 0; i <= 100; i += 10) {
        updateStep("2", { progress: i });
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      updateStep("2", { status: "completed" });

      // Step 3: Comparing Keywords
      updateStep("3", { status: "in-progress" });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      updateStep("3", { status: "completed" });

      // Step 4: Generating Insights
      updateStep("4", { status: "in-progress" });

      // Use the REAL job description if provided, otherwise use Market DNA context
      // FALLBACK logic: If no job description is provided, the backend/hybrid service 
      // will use the Market DNA context and Target Role from resume basics.
      const effectiveJobDescription = jobDescription.trim();
      
      await analyzeResume(resume.data, effectiveJobDescription, jobTitle, "mid-level");

      updateStep("4", { status: "completed" });

      // Step 5: Complete
      updateStep("5", { status: "in-progress" });
      await new Promise((resolve) => setTimeout(resolve, 500));
      updateStep("5", { status: "completed" });

      // Show results after brief delay
      setTimeout(() => {
        setIsStartingAnalysis(false);
        setShowAnalysis(true);
      }, 800);
    } catch {
      // Mark current step as error
      const currentStepIndex = analysisSteps.findIndex((step) => step.status === "in-progress");
      if (currentStepIndex !== -1) {
        updateStep(analysisSteps[currentStepIndex].id, { status: "error" });
      }

      setTimeout(() => {
        setIsStartingAnalysis(false);
      }, 1000);
      // Error handling is done by the hook itself
    }
  };

  if (showAnalysis) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Brain className="size-5" />
            {t`AI Analysis`}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowAnalysis(false);
            }}
          >
            {t`Back`}
          </Button>
        </div>
        <ModernResumeAnalysisDashboard
          analysisData={analysisData}
          loading={loading}
          clearAnalysis={clearAnalysis}
        />
      </div>
    );
  }

  return (
    <Card className="analysis-section-card border-2 border-dashed border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50 dark:border-purple-700 dark:from-purple-900/20 dark:to-blue-900/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg font-semibold">
          <Brain className="size-5 text-purple-600" />
          {t`AI Resume Analysis`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isStartingAnalysis ? (
          // Progress indicator state
          <div className="space-y-6 py-4">
            <div className="space-y-2 text-center">
              <Brain className="mx-auto size-8 animate-pulse text-purple-600" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                {t`Analyzing Your Resume`}
              </h3>
            </div>

            <EnhancedProgressIndicator steps={analysisSteps} className="px-2" />
          </div>
        ) : (
          // Normal content state
          <>
            {/* Market DNA Available Banner */}
            {hasMarketDNA && (
              <div className="flex flex-col gap-3 rounded-xl bg-gradient-to-r from-purple-100 to-blue-100 p-3 dark:from-purple-900/40 dark:to-blue-900/40">
                <div className="flex items-center gap-3">
                  <Lightning className="size-6 text-purple-600" weight="fill" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-purple-800 dark:text-purple-200">
                      {t`Market DNA Available`}
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-300">
                      {t`We'll use saved market intelligence for context-aware analysis.`}
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0"
                    onClick={() => setShowMarketDetails(!showMarketDetails)}
                  >
                    {showMarketDetails ? <CaretUp /> : <CaretDown />}
                  </Button>
                </div>

                {showMarketDetails && (
                  <div className="space-y-3 border-t border-purple-200 pt-3 dark:border-purple-700/50">
                    {/* Hard Skills */}
                    {marketDNA.top_hard_skills?.length > 0 && (
                      <div className="space-y-1">
                        <Label className="flex items-center gap-1.5 text-xs text-purple-700 dark:text-purple-300">
                          <CheckCircle className="size-4 text-purple-600" weight="fill" />
                          {t`Top Tech Skills`}
                        </Label>
                        <div className="flex flex-wrap gap-1.5">
                          {marketDNA.top_hard_skills.map((skill: string) => (
                            <span key={skill} className="rounded-full bg-purple-100/80 px-2 py-0.5 text-[10px] font-medium text-purple-800 dark:bg-purple-800/40 dark:text-purple-200">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Soft Skills */}
                    {marketDNA.top_soft_skills?.length > 0 && (
                      <div className="space-y-1">
                        <Label className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300">
                          <Lightning className="size-4 text-blue-600" weight="fill" />
                          {t`Power Skills`}
                        </Label>
                        <div className="flex flex-wrap gap-1.5">
                          {marketDNA.top_soft_skills.map((skill: string) => (
                            <span key={skill} className="rounded-full bg-blue-100/80 px-2 py-0.5 text-[10px] font-medium text-blue-800 dark:bg-blue-800/40 dark:text-blue-200">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hidden Requirements */}
                    {marketDNA.hidden_requirements?.length > 0 && (
                      <div className="space-y-1">
                        <Label className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                          <Lightbulb className="size-4 text-amber-600" weight="fill" />
                          {t`Hidden Keywords`}
                        </Label>
                        <div className="flex flex-col gap-1 rounded-lg bg-amber-50/50 p-2 dark:bg-amber-900/10">
                          {marketDNA.hidden_requirements.map((req: string, i: number) => (
                            <div key={i} className="flex items-start gap-1.5 text-[10px] text-amber-800 dark:text-amber-200">
                              <span className="mt-1 flex-shrink-0">•</span>
                              <span>{req}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Interview Focus */}
                    {marketDNA.interview_focus && (
                      <div className="space-y-1">
                        <Label className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
                          <Target className="size-4 text-emerald-600" weight="fill" />
                          {t`Interview Focus`}
                        </Label>
                        <p className="rounded-lg bg-emerald-50/50 p-2 text-[10px] italic leading-relaxed text-emerald-800 dark:bg-emerald-900/10 dark:text-emerald-200">
                          {marketDNA.interview_focus}
                        </p>
                      </div>
                    )}

                    {/* Salary Range */}
                    {marketDNA.salary_range && (
                      <div className="flex items-center justify-between rounded-lg bg-white/60 p-2 shadow-sm dark:bg-black/20">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">
                          <CurrencyDollar className="size-4 text-emerald-600" weight="bold" />
                          {t`Avg. Market Salary`}
                        </div>
                        <span className="text-xs font-black text-emerald-700 dark:text-emerald-400">
                          {marketDNA.salary_range}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Job Description Input - Optional when Market DNA exists */}
            <div className="space-y-2">
              <Label htmlFor="job-description" className="flex items-center gap-2 text-sm font-medium">
                <FileText className="size-4 text-purple-600" />
                {hasMarketDNA ? t`Optional: Paste Job Description` : t`Paste Job Description`}
              </Label>
              <Textarea
                id="job-description"
                placeholder={hasMarketDNA 
                  ? t`Optional: Paste a specific job description if you have one...` 
                  : t`Paste the full job description here...`}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                className="min-h-[100px] resize-none text-sm"
              />
              {!hasMarketDNA && jobDescription.length === 0 && (
                <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  <Warning className="size-4 shrink-0" />
                  <span>{t`A real job description is required for accurate analysis`}</span>
                </div>
              )}
              {jobDescription.length > 0 && (
                <p className="text-xs text-green-600 dark:text-green-400">
                  ✓ {t`${jobDescription.split(/\s+/).length} words detected`}
                </p>
              )}
            </div>

            {/* Feature hints */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-blue-50 p-2 dark:bg-blue-900/20">
                <Target className="mx-auto mb-1 size-4 text-blue-600" />
                <span className="text-[10px] text-blue-700 dark:text-blue-300">{t`Keyword Match`}</span>
              </div>
              <div className="rounded-lg bg-purple-50 p-2 dark:bg-purple-900/20">
                <TrendUp className="mx-auto mb-1 size-4 text-purple-600" />
                <span className="text-[10px] text-purple-700 dark:text-purple-300">{hasMarketDNA ? t`Market DNA` : t`Gap Analysis`}</span>
              </div>
              <div className="rounded-lg bg-green-50 p-2 dark:bg-green-900/20">
                <Lightbulb className="mx-auto mb-1 size-4 text-green-600" />
                <span className="text-[10px] text-green-700 dark:text-green-300">{t`Quick Fixes`}</span>
              </div>
            </div>

            <Button
              disabled={loading || (!hasMarketDNA && jobDescription.trim().length < 30)}
              className="w-full gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg shadow-purple-500/20 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50"
              onClick={handleQuickAnalysis}
            >
              <Brain className="size-4" />
              {loading 
                ? t`Analyzing...` 
                : hasMarketDNA 
                  ? (jobDescription.trim().length > 0 ? t`Analyze vs Job & Market DNA` : t`Analyze with Market DNA`) 
                  : jobDescription.trim().length < 30 
                    ? t`Add JD to Start` 
                    : t`Start AI Analysis`}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
